import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { App } from "antd";

import { useEnrollMutation } from "@/redux/service";
import type { EnrollRequest, Track } from "@/redux/certifications/types";
import SwitchPathModal from "@/domains/certifications/components/SwitchPathModal";
import { getContentKey } from "@/domains/certifications/utils";
import {
  certificationPaths,
  certificationRouteKey,
} from "@/domains/certifications/routes";
import Hero from "@/features/certifications/details/components/Hero";
import WhatYouGain from "@/features/certifications/details/components/WhatYouGain";
import HowItWorks from "@/features/certifications/details/components/HowItWorks";
import PathsSection from "@/features/certifications/details/components/PathsSection";
import { CertificationDetailsProps } from "@/features/certifications/details/types";

import "./styles.css";

const CertificationDetails = ({ certification }: CertificationDetailsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const [enroll, { isLoading, originalArgs }] = useEnrollMutation();
  const [switchTarget, setSwitchTarget] = useState<Track | null>(null);

  const contentKey = getContentKey(certification.type);
  const enrollingTrackId = isLoading ? (originalArgs?.trackId ?? null) : null;

  const handleStart = async (trackId: EnrollRequest["trackId"]) => {
    try {
      await enroll({ certificationSlug: certification.slug, trackId }).unwrap();
      navigate(
        certificationPaths.overview(certificationRouteKey(certification)),
      );
    } catch {
      notification.error({
        message: t("certification.details.enrollError"),
      });
    }
  };

  return (
    <div className="certification-details-page">
      <Hero certification={certification} contentKey={contentKey} />

      <div className="certification-details-content flex flex-col gap-800">
        <WhatYouGain contentKey={contentKey} />

        <HowItWorks modules={certification.modules} contentKey={contentKey} />

        <PathsSection
          certification={certification}
          contentKey={contentKey}
          enrollingTrackId={enrollingTrackId}
          onStart={handleStart}
          onSwitch={setSwitchTarget}
          onContinue={() =>
            navigate(
              certificationPaths.overview(certificationRouteKey(certification)),
            )
          }
        />
      </div>

      <SwitchPathModal
        open={switchTarget !== null}
        certification={certification}
        targetTrack={switchTarget}
        onClose={() => setSwitchTarget(null)}
        onSwitched={() =>
          navigate(
            certificationPaths.overview(certificationRouteKey(certification)),
          )
        }
      />
    </div>
  );
};

export default CertificationDetails;
